import React, { Suspense } from 'react';
import PT from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import DirectedAcyclicGraph from './DirectedAcyclicGraph';
import { WEB_APP_PATH } from 'stash/constants';
import { isProd } from 'stash/utils';
import { Route, Switch } from 'react-router-dom';
import { selectSignUpState } from 'stash/selectors';
import SignUpTransition from './SignUpTransition';

const isProduction = isProd();

export const mapStateToProps = (state) => {
	return {
		store: selectSignUpState(state),
	};
};
export class SignUpGraph extends React.Component {
	static propTypes = {
		location: PT.object,
		history: PT.object.isRequired,
		store: PT.object.isRequired,
		product: PT.string,
		graph: PT.shape({
			steps: PT.shape({
				step: PT.shape({
					path: PT.string.isRequired,
					component: PT.string.isRequired,
					redirectToWebApp: PT.bool,
					props: PT.object,
				}),
			}).isRequired,
			transitions: PT.object.isRequired,
		}).isRequired,
		options: PT.shape({
			quiet: PT.bool,
			cyclic: PT.bool,
		}),
	};

	static defaultProps = {
		store: {},
		graph: {
			steps: {},
			transitions: {},
		},
		options: {
			quiet: isProduction, // suppress graph errors on prod
			cyclic: isProduction, // suppress cycle checks on prod
		},
	};

	constructor(props) {
		super(props);

		this.getLatestStep = this.getLatestStep.bind(this);
		this.getNextStep = this.getNextStep.bind(this);
		this.getPathToSteps = this.getPathToSteps.bind(this);
		this.completeStep = this.completeStep.bind(this);
		this.navigateToNextPath = this.navigateToNextPath.bind(this);
		this._preloadAllSteps = this._preloadAllSteps.bind(this);
		this._preloadNextSteps = this._preloadNextSteps.bind(this);
		this.signUpPrefix = window.location.pathname.split('/')[1] || 'sign-up';
		this.paths = this.getPathToSteps();

		this.state = {
			currentStep: {},
			graphSteps: props.graph.steps,
		};

		this.preloadedSteps = {};

		this.graph = new DirectedAcyclicGraph(
			Object.keys(this.props.graph.steps),
			props.graph.transitions,
			{
				quiet: props.options.quiet,
				cyclic: props.options.cyclic,
			}
		);
	}

	componentDidMount() {
		const { history } = this.props;
		const currentStep = this.getLatestStep();

		const currentScreen = this.props.graph.steps[currentStep];
		const resumePath = `/${this.signUpPrefix}${currentScreen.path}`;

		this.setState({
			currentStep,
		});

		if (history.location.pathname !== resumePath) {
			history.push(resumePath);
		}
	}

	_preloadAllSteps() {
		const { graphSteps } = this.state;

		Object.keys(graphSteps).forEach((stepName) => {
			const step = graphSteps[stepName];

			if (!this.preloadedSteps[stepName] && step?.component?.preload) {
				this.preloadedSteps[stepName] = true;
				step.component.preload();
			}
		});
	}

	_preloadNextSteps(nextNode) {
		const { graphSteps } = this.state;

		const nextGraphNode = this.graph.graph[nextNode];
		nextGraphNode.transitions.forEach((transition) => {
			const stepName = transition.nextNode;
			const stepNode = graphSteps[stepName];

			if (!this.preloadedSteps[stepName] && stepNode?.component?.preload) {
				this.preloadedSteps[stepName] = true;
				stepNode.component.preload();
			}
		});
	}

	getLatestStep() {
		const nextNode = this.graph.getLatestNode(this.props.store);
		this._preloadNextSteps(nextNode);
		return nextNode;
	}

	getNextStep() {
		const { store, location } = this.props;
		const currentLocationStep = '/' + location.pathname.split('/').pop();
		const currentStep = this.paths[currentLocationStep];
		const nextNode = this.graph.getNextNode(store, currentStep);

		this._preloadNextSteps(nextNode);
		return nextNode;
	}

	getPathToSteps() {
		return Object.keys(this.props.graph.steps).reduce((acc, step) => {
			return {
				...acc,
				[this.props.graph.steps[step].path]: step,
			};
		}, {});
	}

	navigateToNextPath(nextPath) {
		const { history } = this.props;

		// Scroll back up for small-screen mobile devices.
		window.scrollY && window.scrollTo && window.scrollTo(0, 0);
		history.push(nextPath);
	}

	completeStep(options) {
		const { nextGraph = {}, redirectToWebApp } = options;

		if (redirectToWebApp) {
			return (window.location.href = WEB_APP_PATH);
		}

		// Wait for all redux actions in callstack to complete.
		window.setTimeout(() => {
			if (nextGraph.step && nextGraph.path) {
				return this.setState({ currentStep: nextGraph.step }, () => {
					this.navigateToNextPath(`/${this.signUpPrefix}${nextGraph.path}`);
				});
			}

			const nextStep = this.getNextStep();

			this.setState({ currentStep: nextStep }, () => {
				const nextScreen = this.props.graph.steps[nextStep];
				const nextPath = `/${this.signUpPrefix}${nextScreen.path}`;
				this.navigateToNextPath(nextPath);
			});
		}, 0);
	}

	render() {
		const { graphSteps } = this.state;

		const { location } = this.props;
		// TODO: discuss Suspense fallback with Eng and PM
		return (
			<SignUpTransition location={location}>
				<Suspense fallback={<div></div>}>
					<section>
						<Switch location={location}>
							{Object.values(graphSteps).map((step) => {
								return (
									<Route exact key={step.path} path={`/sign-up*${step.path}`}>
										<step.component
											product={this.props.product}
											location={this.props.location}
											{...step.props}
											completeStep={() => {
												this.completeStep({
													nextGraph: step.nextGraph,
													redirectToWebApp: !!step.redirectToWebApp,
												});
											}}
										/>
									</Route>
								);
							})}
						</Switch>
					</section>
				</Suspense>
			</SignUpTransition>
		);
	}
}

export default connect(mapStateToProps)(withRouter(SignUpGraph));
